import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
} from '@mui/material';

import { DeliveryDetails } from './index';
import { DateTime, DateTimeFormatOptions } from 'luxon';

const FAQ_KEYS = ['when', 'how', 'what', 'allergies'] as const;

type FAQ_Keys = (typeof FAQ_KEYS)[number];

type FAQ_Map = Record<
  FAQ_Keys,
  (
    totalDeliveries: number,
    deliveriesPerWeeK: number,
    firstDeliveryDate: string,
    estimatedFirstDeliveryDate?: string,
  ) => string
>;

const FAQ_QUESTIONS: FAQ_Map = {
  when: () => 'When will my meals be delivered?',
  how: () => 'How do I track my deliveries?',
  what: () => 'What type of food can I expect?',
  allergies: () => 'What if I have allergies?',
};

const LOCAL_SHIPPING_ANSWERS: FAQ_Map = {
  when: (totalDeliveries, deliveriesPerWeeK, firstDeliveryDate) => {
    return `Your${totalDeliveries > 1 ? ' first' : ''} delivery will arrive on ${firstDeliveryDate}.${deliveriesPerWeeK > 1 ? ` Meals are delivered by local couriers every Sunday and Wednesday.` : ''}`;
  },
  how: () =>
    'On the day of delivery you’ll receive text messages with a delivery reminder, approximate drop-off time and confirmation.',
  what: () =>
    'Meals will vary each delivery and week. Each meal includes a full list of ingredients and nutrition fact panel so you have all the information you need to decide when and how to eat your meals. Meals are best consumed within four days of delivery.',
  allergies: () =>
    'We are not yet able to accommodate customers with allergens.  If you have further questions, please reach out to customer support via email or chat.',
};

const RSE_SHIPPING_ANSWERS: FAQ_Map = {
  when: (
    totalDeliveries,
    _deliveriesPerWeeK,
    firstDeliveryDate,
    estimatedFirstDeliveryDate,
  ) =>
    `Your${totalDeliveries > 1 ? ' first' : ''} delivery will arrive on ${estimatedFirstDeliveryDate || firstDeliveryDate}.`,
  how: () =>
    'On Sunday, you’ll receive an email with a tracking number and steps to sign up for alerts from the shipping carrier.',
  what: () =>
    'Meals will vary each delivery and week. Each meal includes a full list of ingredients and nutrition fact panel so you have all the information you need to decide when and how to eat your meals. Meals arrive in sealed packages and are fresh for seven days.',
  allergies: () =>
    'We are not yet able to accommodate customers with allergens.  If you have further questions, please reach out to customer support via email or chat.',
};

const AccordionSection: React.FC<{
  title: string;
  details: string;
  disabled: boolean;
}> = ({ title, details, disabled }) => {
  return (
    <Accordion
      disableGutters
      sx={{
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} disabled={disabled}>
        <Typography variant="body1">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body1">{details}</Typography>
      </AccordionDetails>
    </Accordion>
  );
};

const dateDisplayFormat: DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
};

const FrequentlyAskedQuestions: React.FC<{
  deliveryDetails: DeliveryDetails;
  totalDeliveries: number;
  deliveriesPerWeek: number;
}> = ({ deliveriesPerWeek, deliveryDetails, totalDeliveries }) => {
  const firstDelivery = deliveryDetails.upcomingDeliveries[0];
  const formattedFirstDeliveryDate = DateTime.fromISO(
    firstDelivery.deliveryDate,
    { zone: 'utc' },
  ).toLocaleString(dateDisplayFormat);
  const formattedEstimatedFirstDeliveryDate =
    firstDelivery.estimatedDeliveryDate
      ? DateTime.fromISO(firstDelivery.estimatedDeliveryDate, {
          zone: 'utc',
        }).toLocaleString(dateDisplayFormat)
      : undefined;

  return (
    <Grid
      sx={{
        flexDirection: 'column',
        textAlign: 'center',
      }}
      container
      spacing={3}
    >
      <Grid>
        <Typography variant="h5">Frequently Asked Questions</Typography>
      </Grid>
      <Grid
        sx={{
          textAlign: 'left',
          margin: 'auto',
        }}
      >
        {FAQ_KEYS.map((k) => {
          return (
            <AccordionSection
              key={k}
              title={FAQ_QUESTIONS[k](
                totalDeliveries,
                deliveriesPerWeek,
                formattedFirstDeliveryDate,
                formattedEstimatedFirstDeliveryDate,
              )}
              details={
                deliveryDetails?.type === 'local'
                  ? LOCAL_SHIPPING_ANSWERS[k](
                      totalDeliveries,
                      deliveriesPerWeek,
                      formattedFirstDeliveryDate,
                    )
                  : RSE_SHIPPING_ANSWERS[k](
                      totalDeliveries,
                      deliveriesPerWeek,
                      formattedFirstDeliveryDate,
                      formattedEstimatedFirstDeliveryDate,
                    )
              }
              disabled={!deliveryDetails}
            />
          );
        })}
      </Grid>
    </Grid>
  );
};

export default FrequentlyAskedQuestions;
